


import {
  defineComponent,
  computed,
  onMounted,
  reactive,
  ref,
  watch,
} from '@vue/composition-api';

import { SearchState } from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamMap';
import { useStore } from '@/hooks/useStore';
import { useRoute } from '@/hooks/useRoute';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import RealtimeView from './components/RealtimeView/index.vue';
import ReportsView from './components/ReportsView/index.vue';
import ManualDownloadLinks from './components/common/ManualDownloadLinks/index.vue';
import MapMiscInfo from './components/common/MapMiscInfo.vue';
import { dtFormat } from '@/lib/dateHelper';
import { initSearchConds, validSearchDateRange } from '@/components/ExternalReports/EmergencyResponseTeam/utils';
import {
  VIEW_MODE_REALTIME,
  VIEW_MODE_REPORT,
} from '@/components/ExternalReports/EmergencyResponseTeam/consts';
import {
  ITEMS_PER_PAGE_DEFAULT,
  CURRENT_PAGE_DEFAULT,
} from '@/consts/externalReports/emergencyResponseTeam/report_print';
import { EXTERNAL_REPORT_TYPE } from '@/consts/externalReports/emergencyResponseTeam';
import {
  EXTERNAL_REPORT_ABILITY_DRIVER,
  COMPANY_KIND_HONSHA,
  COMPANY_KIND_KYOKU,
  COMPANY_KIND_PARTNER,
} from '@/consts/externalReports/external_reports';
import useEmergencyResponseTeamCars, {
  provideEmergencyResponseTeamCars,
} from '@/components/ExternalReports/EmergencyResponseTeam/composables/useCars';
import {
  provideEmergencyResponseTeamReports,
  useEmergencyResponseTeamReports,
} from '@/components/ExternalReports/EmergencyResponseTeam/composables/useReports';
import { useRequestGuard } from '@/composables/useRequestGuard';
import type {
  ExternalReportsKeyName,
  ExternalReportChoices,
  ExternalReportsLabelPc,
  ExternalReportSettings,
} from '@/models/apis/externalReports/external_report_settings';
import type { ExternalReportAbility } from '@/models/externalReports/externalReportAbility';
import type { ExternalReportCompany } from '@/models/externalReports/externalReportCompany';

interface SelectOption {
  id: number;
  name: string;
}

export default defineComponent({
  setup() {
    const viewMode = ref<string>(VIEW_MODE_REALTIME);

    const searchState = reactive<SearchState>({
      searchConds: {
        areaId: null,
        companyId: null,
        companyIds: [],
        hanName: null,
        status: '全て',
        dateFrom: new Date(),
        dateTo: new Date(),
      },
      decidedSearchConds: {
        areaId: null,
        companyId: null,
        companyIds: [],
        hanName: null,
        status: '全て',
        dateFrom: new Date(),
        dateTo: new Date(),
      },
    });
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      console.error(errorMessage);
    });

    const store = useStore();
    const userState = store.state.user;
    const isRealtimeMode = computed<boolean>(() => {
      return viewMode.value === VIEW_MODE_REALTIME;
    });
    const isReportMode = computed<boolean>(() => {
      return viewMode.value === VIEW_MODE_REPORT;
    });
    const userSettings = ref<ExternalReportSettings | null>(null);
    const companies = ref<ExternalReportCompany[] | null>(null);
    const externalReportAbility = ref<ExternalReportAbility | null>(null);

    const userCompany = computed<ExternalReportCompany | null>(() => {
      const companyId = externalReportAbility.value?.company_id;
      if (!companyId || !companies.value) return null;
      return companies.value.find(
        (company: ExternalReportCompany) => Number(company.id) === Number(companyId),
      ) || null;
    });

    const companyScope = computed<{
      kyokus: ExternalReportCompany[];
      partners: ExternalReportCompany[];
    }>(() => {
      const externalReportCompanies: ExternalReportCompany[] = companies.value || [];
      const currentCompany = userCompany.value;
      if (!currentCompany) {
        return { kyokus: [], partners: [] };
      }

      if (currentCompany.kind === COMPANY_KIND_HONSHA) {
        return {
          kyokus: externalReportCompanies.filter(o => o.kind === COMPANY_KIND_KYOKU),
          partners: externalReportCompanies.filter(o => o.kind === COMPANY_KIND_PARTNER),
        };
      }
      if (currentCompany.kind === COMPANY_KIND_KYOKU) {
        return {
          kyokus: [currentCompany],
          partners: externalReportCompanies.filter(o => o.kind === COMPANY_KIND_PARTNER && o.parent_id === currentCompany.id),
        };
      }
      const kyoku = externalReportCompanies.find(o => o.id === currentCompany.parent_id && o.kind === COMPANY_KIND_KYOKU);
      return {
        kyokus: kyoku ? [kyoku] : [],
        partners: [currentCompany],
      };
    });

    const partnersByKyoku = computed<ExternalReportCompany[]>(() => {
      let partners = companyScope.value.partners;
      if (searchState.searchConds.areaId) {
        partners = partners.filter((o: ExternalReportCompany) => o.parent_id === searchState.searchConds.areaId);
      }
      return partners;
    });

    const scopedPartners = computed<ExternalReportCompany[]>(() => {
      let partners = partnersByKyoku.value;
      if (searchState.searchConds.companyId !== null) {
        partners = partners.filter((o: ExternalReportCompany) => o.id === searchState.searchConds.companyId);
      }
      return partners;
    });
    const reportChoices = computed<ExternalReportChoices>(() => {
      if (!userSettings.value) return {} as ExternalReportChoices;

      return userSettings.value.choices;
    });
    const workHans = computed<SelectOption[]>(() =>
      scopedPartners.value.flatMap((o) =>
        o.partner_han_masters.map((han) => ({
          id: Number(han.id),
          name: han.han_name,
        })),
      ),
    );
    const kyokus = computed<SelectOption[]>(() => companyScope.value.kyokus.map(toOption));
    const partners = computed<SelectOption[]>(() => partnersByKyoku.value.map(toOption));

    watch(
      () => [searchState.searchConds.areaId, partnersByKyoku.value],
      () => {
        if (
          searchState.searchConds.companyId !== null &&
          !partnersByKyoku.value.some(o => o.id === searchState.searchConds.companyId)
        ) {
          searchState.searchConds.companyId = null;
          searchState.searchConds.hanName = null;
        }
      },
    );
    watch(
      () => [searchState.searchConds.companyId, scopedPartners.value],
      () => {
        if (!searchState.searchConds.hanName) return;
        const hanName = searchState.searchConds.hanName;
        const hasHan = scopedPartners.value.some((p: ExternalReportCompany) =>
          p.partner_han_masters.some((han) => han.han_name === hanName),
        );
        if (!hasHan) {
          searchState.searchConds.hanName = null;
        }
      },
    );
    const isDateParamOk = computed<boolean>(() => {
      return validSearchDateRange(searchState.searchConds);
    });
    const statusChoices = computed<ExternalReportsKeyName[]>(() => {
      if (!reportChoices.value?.status) return [];
      const status = reportChoices.value?.status || {};
      return [
        { key: '全て', name: '全て' },
        { key: status.working.key, name: status.working.name },
        { key: status.stopped.key, name: status.stopped.name },
      ];
    });
    const pcLabels = computed<ExternalReportsLabelPc>(() => {
      return userSettings.value?.pc || {
        title: {},
        button: {},
        field_label: {},
        table_header: {},
      } as ExternalReportsLabelPc;
    });

    const { router } = useRoute();

    onMounted(async() => {
      await doRequest(async() => {
        await waitForUserAndMasters();
      }, '初期化に失敗しました。');
      userSettings.value = window.master.externalReportSettings?.find(
        setting => setting.external_report_type === EXTERNAL_REPORT_TYPE,
      ) ?? null;
      companies.value = window.master.externalReportCompanies?.filter(
        company => company.external_report_type === EXTERNAL_REPORT_TYPE,
      ) ?? null;
      externalReportAbility.value = userState?.external_report_abilities?.find(
        ability => ability.external_report_type === EXTERNAL_REPORT_TYPE,
      ) ?? null;

      if (!externalReportAbility.value || !userSettings.value || !companies.value) {
        router.replace({ name: 'Login' });
        return;
      }
      const abilities = userSettings.value.choices.abilities;

      searchState.searchConds = initSearchConds();
      searchState.decidedSearchConds = { ...searchState.searchConds };
      const ability = abilities?.find(ability => ability.key === externalReportAbility.value?.ability_id);
      if (!ability) {
        router.replace({ name: 'Login' });
        return;
      }

      if (ability.name.endsWith(EXTERNAL_REPORT_ABILITY_DRIVER)) {
        router.replace({ name: 'Login' });
      }
    });

    const toOption = (o: { id: number; name: string }): SelectOption => ({ id: Number(o.id), name: o.name });

    const applyCompanyFiltersForSearch = () => {
      const hasSelection = searchState.searchConds.areaId !== null || searchState.searchConds.companyId !== null;
      searchState.searchConds.companyIds = hasSelection ? scopedPartners.value.map(p => Number(p.id)) : [];
    };

    const {
      filterCars,
      clearCars,
    } = (() => {
      const injectedState = provideEmergencyResponseTeamCars(searchState);
      return useEmergencyResponseTeamCars(injectedState);
    })();

    const {
      searchReports,
      clearReports,
    } = (() => {
      const reportState = provideEmergencyResponseTeamReports(searchState);
      return useEmergencyResponseTeamReports(reportState);
    })();

    const filterCarsForSearch = (): void => {
      applyCompanyFiltersForSearch();
      filterCars();
    };
    const searchReportsForSearch = (): void => {
      applyCompanyFiltersForSearch();
      searchReports();
    };
    const changeViewMode = async(mode: string) => {
      if (mode === viewMode.value) return;

      searchState.searchConds = initSearchConds();
      searchState.decidedSearchConds = { ...searchState.searchConds };
      viewMode.value = mode;
      if (mode === VIEW_MODE_REALTIME) {
        clearReports();
      }
      if (mode === VIEW_MODE_REPORT) {
        clearCars();
      }
    };

    const onClickReportPrintAllButton = () => {
      const routeObj = {
        name: 'PrintExternalReportsEmergencyResponseTeamReportAll',
        params: {
          dateFrom: dtFormat(searchState.searchConds.dateFrom, 'yyyy-mm-dd'),
          dateTo: dtFormat(searchState.searchConds.dateTo, 'yyyy-mm-dd'),
          hanName: searchState.searchConds.hanName || 'all',
          currentPage: CURRENT_PAGE_DEFAULT.toString(),
          itemsPerPage: ITEMS_PER_PAGE_DEFAULT.toString(),
        },
      };
      const obj = router.resolve(routeObj);
      window.open(obj.href, '_blank');
    };

    return {
      searchState,
      userState,
      isRequesting,
      // const
      statusChoices,
      userSettings,
      VIEW_MODE_REALTIME,
      VIEW_MODE_REPORT,
      // computed
      isRealtimeMode,
      isReportMode,
      workHans,
      kyokus,
      companies: partners,
      isDateParamOk,
      pcLabels,
      // methods
      changeViewMode,
      filterCarsForSearch,
      searchReportsForSearch,
      onClickReportPrintAllButton,
    };
  },
  components: {
    RealtimeView,
    ReportsView,
    ManualDownloadLinks,
    MapMiscInfo,
  },
});
